import { Parameter } from '@shared/types';
import { useState, useMemo } from 'react';
import { CodeSnapshot, validateParameterValue } from '@/utils/parameterUtils';

export interface ParameterSnapshot {
  id: string;
  name: string;
  timestamp: number;
  parameters: Parameter[];
  /** 'code' for snapshots defined in SCAD source, 'user' for runtime-created ones */
  source: 'code' | 'user';
}

/**
 * Coerces a raw string value from a snapshot definition into the correct type
 * for a given parameter. Mirrors the JSON import logic in ParameterSection.
 */
function coerceSnapshotValue(
  param: Parameter,
  rawValue: string,
): Parameter['value'] {
  // Strip surrounding quotes for string-typed params
  const unquoted = rawValue.replace(/^["']|["']$/g, '');

  if (param.type === 'boolean') {
    return unquoted === 'true';
  }

  if (param.type === 'number' || !param.type) {
    const num = Number(rawValue);
    return isNaN(num) ? param.defaultValue : num;
  }

  if (param.type === 'number[]') {
    // Expect "[1, 2, 3]" format
    try {
      const arr = JSON.parse(rawValue);
      if (Array.isArray(arr)) return arr.map(Number);
    } catch {
      // fall through
    }
    return param.defaultValue;
  }

  if (param.type === 'boolean[]') {
    try {
      const arr = JSON.parse(rawValue);
      if (Array.isArray(arr)) return arr.map(Boolean);
    } catch {
      // fall through
    }
    return param.defaultValue;
  }

  // string, color, font, file — use unquoted value
  return validateParameterValue(param, unquoted);
}

/**
 * Merges a CodeSnapshot's raw values onto the live parameters array,
 * producing a full Parameter[] suitable for storing as a snapshot.
 */
function applyCodeSnapshot(
  codeSnapshot: CodeSnapshot,
  parameters: Parameter[],
): Parameter[] {
  return parameters.map((param) => {
    if (param.name in codeSnapshot.values) {
      const rawValue = codeSnapshot.values[param.name];
      return {
        ...param,
        value: coerceSnapshotValue(param, rawValue),
      };
    }
    return param;
  });
}

export function useParameterSnapshots(
  parameters: Parameter[],
  codeSnapshots: CodeSnapshot[] = [],
) {
  const [userSnapshots, setUserSnapshots] = useState<ParameterSnapshot[]>([]);

  // Create a signature of the parameter definitions to detect schema changes
  const paramDefinitionSignature = useMemo(() => {
    return parameters
      .map(
        (p) =>
          `${p.name}:${p.type}:${p.group}:${JSON.stringify(p.range)}:${JSON.stringify(p.options)}`,
      )
      .join('|');
  }, [parameters]);

  const [lastSignature, setLastSignature] = useState(paramDefinitionSignature);

  // Clear user snapshots if parameter schema changes
  if (paramDefinitionSignature !== lastSignature) {
    setUserSnapshots([]);
    setLastSignature(paramDefinitionSignature);
  }

  // Derive code-defined snapshots fresh from current parameters + definitions.
  // These are never stored in state — they're always in sync with the source.
  const derivedCodeSnapshots = useMemo<ParameterSnapshot[]>(() => {
    return codeSnapshots.map((cs) => ({
      id: `code::${cs.name}`,
      name: cs.name,
      timestamp: 0,
      source: 'code' as const,
      parameters: applyCodeSnapshot(cs, parameters),
    }));
  }, [codeSnapshots, parameters]);

  // Combined list: code-defined first, then user-created
  const snapshots = useMemo(
    () => [...derivedCodeSnapshots, ...userSnapshots],
    [derivedCodeSnapshots, userSnapshots],
  );

  const createSnapshot = (name: string) => {
    const newSnapshot: ParameterSnapshot = {
      id: crypto.randomUUID(),
      name: name || new Date().toLocaleString(),
      timestamp: Date.now(),
      source: 'user',
      parameters: JSON.parse(JSON.stringify(parameters)), // Deep copy
    };
    setUserSnapshots((prev) => [...prev, newSnapshot]);
    return newSnapshot;
  };

  const applySnapshot = (snapshotId: string) => {
    const snapshot = snapshots.find((s) => s.id === snapshotId);
    if (snapshot) {
      return snapshot.parameters;
    }
    return null;
  };

  return {
    snapshots,
    createSnapshot,
    applySnapshot,
  };
}
